import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiInfo, FiZap } from 'react-icons/fi';
import cn from 'classnames';
import type { CalloutKind } from '../markdown/alerts';

const STYLES: Record<
  CalloutKind,
  {
    box: string;
    label: string;
    Icon: React.ComponentType<{ size?: number; 'aria-hidden'?: string }>;
  }
> = {
  tip: {
    box: 'border-blue-500 bg-blue-50 text-blue-900 dark:border-blue-400 dark:bg-blue-900/40 dark:text-blue-100',
    label: 'text-blue-700 dark:text-blue-300',
    Icon: FiZap,
  },
  note: {
    box: 'border-gray-400 bg-gray-50 text-gray-800 dark:border-gray-500 dark:bg-gray-800/70 dark:text-gray-100',
    label: 'text-gray-600 dark:text-gray-300',
    Icon: FiInfo,
  },
  warning: {
    box: 'border-amber-500 bg-amber-50 text-amber-900 dark:border-amber-400 dark:bg-amber-900/30 dark:text-amber-100',
    label: 'text-amber-700 dark:text-amber-300',
    Icon: FiAlertTriangle,
  },
};

export const Callout: React.FC<{
  kind: CalloutKind;
  children: React.ReactNode;
}> = ({ kind, children }) => {
  const { t } = useTranslation('HelpGuide');
  const { box, label, Icon } = STYLES[kind];
  return (
    <div
      role="note"
      data-callout={kind}
      className={cn(
        'my-4 rounded-r-lg border-l-4 px-4 py-3 text-sm leading-6',
        box
      )}
    >
      <div
        className={cn(
          'mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide',
          label
        )}
      >
        <Icon size={14} aria-hidden="true" />
        {t(`callout.${kind}`)}
      </div>
      <div className="[&>p]:m-0 [&>p+p]:mt-2">{children}</div>
    </div>
  );
};
